import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Loader2, Trophy, Flag, Timer, Gauge, MapPin, Car, Share2, Globe2, Lock, Users,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar,
} from "recharts";
import { simhubAPI } from "@/lib/api";

const formatLapTime = (ms) => {
  if (!ms || ms <= 0) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
};
const formatDeltaMs = (ms) => {
  if (ms == null) return "—";
  const sign = ms > 0 ? "+" : "";
  return `${sign}${(ms / 1000).toFixed(3)}s`;
};

const LAP_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#84cc16"];
const COMPARE_COLOR = "#f97316";

function StatBlock({ icon: Icon, label, value, hint, tone }) {
  return (
    <Card className={`border-border bg-card p-3 ${tone === "accent" ? "ring-1 ring-primary/40" : ""}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="mt-1 font-display text-xl font-bold leading-none">{value ?? "—"}</p>
      {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
    </Card>
  );
}

// --- Lap analytics helpers ---------------------------------------------------
function analyseLap(lap) {
  const samples = lap?.samples || [];
  if (!samples.length) return null;
  const speeds = samples.map(s => s.speed || 0).filter(Number.isFinite);
  const throttles = samples.map(s => s.throttle || 0);
  const brakes = samples.map(s => s.brake || 0);

  const minSpeed = speeds.length ? Math.min(...speeds) : 0;
  const maxSpeed = speeds.length ? Math.max(...speeds) : 0;
  const avgSpeed = speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;

  // % of samples at various input states
  const fullThrottle = throttles.filter(v => v >= 95).length / samples.length;
  const anyThrottle = throttles.filter(v => v > 5).length / samples.length;
  const anyBrake = brakes.filter(v => v > 5).length / samples.length;
  const coasting = samples.filter(s => (s.throttle || 0) <= 5 && (s.brake || 0) <= 5).length / samples.length;
  const trailBraking = samples.filter(s => (s.throttle || 0) > 5 && (s.brake || 0) > 5).length / samples.length;
  const avgPeakBrake = brakes.filter(v => v > 60).length
    ? brakes.filter(v => v > 60).reduce((a, b) => a + b, 0) / brakes.filter(v => v > 60).length
    : 0;

  // Brake-event count = contiguous segments with brake > 15 lasting > 300ms.
  let brakeEvents = 0;
  let inEvent = false;
  let eventStart = 0;
  for (let i = 0; i < samples.length; i++) {
    const b = samples[i].brake || 0;
    if (b > 15 && !inEvent) { inEvent = true; eventStart = samples[i].t; }
    if ((b <= 15 || i === samples.length - 1) && inEvent) {
      inEvent = false;
      if (samples[i].t - eventStart > 300) brakeEvents++;
    }
  }

  // Gear distribution (if present)
  const gearCounts = {};
  samples.forEach(s => {
    if (s.gear === undefined) return;
    const key = s.gear;
    gearCounts[key] = (gearCounts[key] || 0) + 1;
  });
  const gearDistribution = Object.keys(gearCounts).length
    ? Object.entries(gearCounts).map(([g, c]) => ({ gear: g === "-1" ? "R" : g === "0" ? "N" : `G${g}`, pct: Math.round((c / samples.length) * 100) }))
    : null;

  return {
    minSpeed, maxSpeed, avgSpeed, fullThrottle, anyThrottle, anyBrake, coasting, trailBraking, avgPeakBrake, brakeEvents, gearDistribution,
  };
}

// Combine lap samples into chart-ready points keyed by x-axis unit
function buildMetricData(laps, metric, xAxis, compareLap) {
  const points = {};
  const addSeries = (lap, dataKey) => {
    if (!lap?.samples) return;
    const total = lap.samples.length ? lap.samples[lap.samples.length - 1].t || 1 : 1;
    lap.samples.forEach((p) => {
      let x = 0;
      if (xAxis === "time") {
        x = Math.round((p.t || 0) / 500) * 500;
      } else {
        // position by t if `pos` isn't available
        const pct = p.pos != null && p.pos > 0 ? p.pos : (p.t / total) * 100;
        x = Math.round(pct);
      }
      if (!points[x]) points[x] = { x };
      points[x][dataKey] = p[metric];
    });
  };
  laps.forEach(l => addSeries(l, `lap${l.lap_number}`));
  if (compareLap) addSeries(compareLap, "compare");
  return Object.values(points).sort((a, b) => a.x - b.x);
}

function ChartCard({ title, data, selectedLaps, unit, domain, xAxis, hasCompare }) {
  if (!data || data.length === 0) {
    return (
      <Card className="border-border bg-card p-4">
        <p className="font-display text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</p>
        <p className="mt-2 text-xs text-muted-foreground">No samples for the selected laps.</p>
      </Card>
    );
  }
  const xFormatter = xAxis === "time" ? (v) => `${(v / 1000).toFixed(0)}s` : (v) => `${v}%`;
  const xLabelFormatter = xAxis === "time" ? (v) => `${(v / 1000).toFixed(1)}s` : (v) => `${v}% lap`;
  return (
    <Card className="border-border bg-card p-3">
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-wide">{title}</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="x" tickFormatter={xFormatter} stroke="hsl(var(--muted-foreground))" fontSize={10} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={domain || ["dataMin", "dataMax"]} unit={unit} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
            labelFormatter={xLabelFormatter}
            formatter={(v) => `${v}${unit}`}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {selectedLaps.map((lapNum, i) => (
            <Line key={lapNum} type="monotone" dataKey={`lap${lapNum}`} name={`Lap ${lapNum}`}
              stroke={LAP_COLORS[i % LAP_COLORS.length]} dot={false} strokeWidth={2} connectNulls />
          ))}
          {hasCompare && (
            <Line type="monotone" dataKey="compare" name="Comparison" stroke={COMPARE_COLOR} dot={false} strokeWidth={2} strokeDasharray="5 3" connectNulls />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function GearChart({ dist }) {
  if (!dist) return null;
  return (
    <Card className="border-border bg-card p-3">
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-wide">Gear usage (% lap time)</p>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={dist} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="gear" stroke="hsl(var(--muted-foreground))" fontSize={10} />
          <YAxis unit="%" stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 100]} />
          <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
          <Bar dataKey="pct" fill="hsl(var(--primary))" />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

function AnalyticsPanel({ lap, bestLap }) {
  const stats = useMemo(() => analyseLap(lap), [lap]);
  if (!stats) return null;
  const pct = (n) => `${Math.round(n * 100)}%`;
  const sectorBest = {
    1: bestLap?.sector1_ms, 2: bestLap?.sector2_ms, 3: bestLap?.sector3_ms,
  };
  return (
    <Card className="border-border bg-card p-4" data-testid="session-analytics-panel">
      <p className="mb-3 font-display text-sm font-bold uppercase tracking-wide">Lap {lap.lap_number} analytics</p>
      <div className="grid gap-2 sm:grid-cols-4">
        <StatBlock label="Lap time" value={formatLapTime(lap.lap_time_ms)} hint={bestLap && lap.lap_number === bestLap.lap_number ? "BEST" : `Δ ${formatDeltaMs(lap.lap_time_ms - (bestLap?.lap_time_ms || lap.lap_time_ms))}`} tone="accent" />
        <StatBlock label="Top speed" value={`${stats.maxSpeed.toFixed(0)} km/h`} hint={`Min ${stats.minSpeed.toFixed(0)} · Avg ${stats.avgSpeed.toFixed(0)}`} />
        <StatBlock label="Full throttle" value={pct(stats.fullThrottle)} hint={`On throttle ${pct(stats.anyThrottle)}`} />
        <StatBlock label="Braking" value={pct(stats.anyBrake)} hint={`${stats.brakeEvents} events · peak ${stats.avgPeakBrake.toFixed(0)}%`} />
        {[1, 2, 3].map((n) => {
          const t = lap[`sector${n}_ms`];
          if (!t) return null;
          const best = sectorBest[n];
          const delta = best ? t - best : null;
          return (
            <StatBlock key={n} label={`Sector ${n}`} value={formatLapTime(t)} hint={delta != null ? `Δ ${formatDeltaMs(delta)}` : null} />
          );
        })}
        <StatBlock label="Coasting" value={pct(stats.coasting)} hint={`Trail-brake ${pct(stats.trailBraking)}`} />
      </div>
      {stats.gearDistribution && (
        <div className="mt-3">
          <GearChart dist={stats.gearDistribution} />
        </div>
      )}
    </Card>
  );
}

function SponsorRibbon({ sponsors }) {
  if (!sponsors?.length) return null;
  return (
    <div className="flex items-center gap-3 overflow-x-auto rounded-md border border-border bg-secondary/40 p-2" data-testid="session-sponsors">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Sponsors</span>
      {sponsors.map((s) => (
        <a key={s.id} href={s.website || "#"} target="_blank" rel="noopener noreferrer" className="flex shrink-0 items-center gap-2 rounded bg-card px-2 py-1 text-xs" title={s.name}>
          <img src={s.logo_url} alt={s.name} className="h-5 w-auto object-contain" loading="lazy" />
          <span className="font-medium">{s.name}</span>
        </a>
      ))}
    </div>
  );
}

function ComparePicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [pub, mine, discover] = await Promise.all([
        simhubAPI.listPublicSessions({ limit: 50 }).catch(() => ({ data: [] })),
        simhubAPI.listApprovedSharedSessions().catch(() => ({ data: [] })),
        simhubAPI.listDiscoverable({ limit: 50 }).catch(() => ({ data: [] })),
      ]);
      const items = [
        ...(pub.data || []).map((s) => ({ ...s, source: "public" })),
        ...(mine.data || []).map((s) => ({ ...s, source: "approved" })),
        ...(discover.data || [])
          .filter((s) => s.access === "locked" || s.access === "pending")
          .map((s) => ({ ...s, source: s.access })),
      ];
      const seen = new Set();
      setList(items.filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true))));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="session-compare-pick">
        <Users className="mr-1 h-4 w-4" /> Compare against another lap
      </Button>
    );
  }
  return (
    <Card className="space-y-2 border-border bg-card p-3" data-testid="session-compare-picker">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold uppercase tracking-wide">Pick a session to compare</p>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Close</Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : list.length === 0 ? (
        <p className="text-xs text-muted-foreground">No public or approved sessions available yet.</p>
      ) : (
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {list.map((s) => {
            const needsRequest = s.source === "locked" || s.source === "pending";
            return (
              <div key={s.id} className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background p-2 text-left text-xs transition-colors hover:border-primary/60">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{s.owner?.username || "Shared"} · {s.track?.track_display_name || s.track?.track_name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{s.car?.car_model}{s.car?.car_class ? ` · ${s.car.car_class}` : ""}</p>
                </div>
                {needsRequest ? (
                  s.source === "pending" ? (
                    <span className="shrink-0 rounded bg-amber-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-400">Requested</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const r = await simhubAPI.createShareRequest(s.id, "Compare request from session detail");
                        toast.success(r.data?.status === "approved" ? "Opened" : "Request sent");
                      } catch (e) { toast.error(e.response?.data?.detail || "Could not request"); }
                    }} data-testid={`session-compare-request-${s.id}`}>
                      <Share2 className="mr-1 h-3 w-3" /> Request
                    </Button>
                  )
                ) : (
                  <button type="button" onClick={() => { onPick(s); setOpen(false); }}
                    className="shrink-0 rounded bg-primary/15 px-3 py-1 text-[11px] font-semibold text-primary hover:bg-primary/25"
                    data-testid={`session-compare-pick-${s.id}`}>
                    Compare
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default function SessionDetailModal({ sessionId, open, onOpenChange, publicView = false }) {
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [selectedLaps, setSelectedLaps] = useState([]);
  const [xAxis, setXAxis] = useState("time");
  const [publishing, setPublishing] = useState(false);
  const [compare, setCompare] = useState(null); // { session, lap }

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    setError("");
    setSession(null);
    setCompare(null);
    const fetcher = publicView ? simhubAPI.getPublicSession(sessionId) : simhubAPI.getSession(sessionId);
    fetcher
      .then((r) => {
        const s = r.data || null;
        setSession(s);
        const laps = (s?.laps || []).filter((l) => l.is_valid !== false && l.lap_time_ms > 0);
        const bestLap = laps.reduce((best, l) => (!best || l.lap_time_ms < best.lap_time_ms ? l : best), null);
        setSelectedLaps(bestLap ? [bestLap.lap_number] : []);
      })
      .catch(() => setError("Could not load this session"))
      .finally(() => setLoading(false));
  }, [open, sessionId, publicView]);

  const laps = session?.laps || [];
  const validLaps = useMemo(() => laps.filter((l) => l.is_valid !== false && l.lap_time_ms > 0), [laps]);
  const bestLap = useMemo(
    () => validLaps.reduce((best, l) => (!best || l.lap_time_ms < best.lap_time_ms ? l : best), null),
    [validLaps],
  );
  const totalMaxSpeed = useMemo(
    () => validLaps.reduce((m, l) => Math.max(m, l.max_speed_kmh || 0), 0),
    [validLaps],
  );
  const avgLapMs = session?.average_lap_time_ms;

  const toggleLap = (lapNumber) => {
    setSelectedLaps((prev) =>
      prev.includes(lapNumber) ? prev.filter((n) => n !== lapNumber) : [...prev, lapNumber],
    );
  };

  const activeLaps = selectedLaps.map((n) => laps.find((l) => l.lap_number === n)).filter(Boolean);
  const compareLap = compare?.lap;
  const speedData = useMemo(() => buildMetricData(activeLaps, "speed", xAxis, compareLap), [activeLaps, xAxis, compareLap]);
  const throttleData = useMemo(() => buildMetricData(activeLaps, "throttle", xAxis, compareLap), [activeLaps, xAxis, compareLap]);
  const brakeData = useMemo(() => buildMetricData(activeLaps, "brake", xAxis, compareLap), [activeLaps, xAxis, compareLap]);
  const anySamples = activeLaps.some((l) => (l.samples || []).length > 0);

  const trackLabel = session?.track?.track_display_name || session?.track?.track_name || "—";
  const carLabel = session?.car?.car_model || "—";
  const carClass = session?.car?.car_class;
  const sponsors = session?.sponsors || [];
  const isOwned = !publicView && session?.user_id;

  const togglePublish = async () => {
    if (!session) return;
    setPublishing(true);
    try {
      if (session.is_public) {
        await simhubAPI.unpublishSession(sessionId);
        setSession({ ...session, is_public: false });
        toast.success("Session is now private");
      } else {
        await simhubAPI.publishSession(sessionId);
        setSession({ ...session, is_public: true });
        toast.success("Session published — you earned points!");
      }
    } catch {
      toast.error("Could not update session visibility");
    } finally {
      setPublishing(false);
    }
  };

  const onPickCompare = async (pick) => {
    try {
      const full = pick.source === "approved"
        ? await simhubAPI.getApprovedSharedSession(pick.id)
        : await simhubAPI.getPublicSession(pick.id);
      const picked = full.data;
      const laps2 = (picked.laps || []).filter((l) => l.is_valid !== false && l.lap_time_ms > 0);
      const best = laps2.reduce((b, l) => (!b || l.lap_time_ms < b.lap_time_ms ? l : b), null);
      setCompare({ session: picked, lap: best });
      if (best) toast.success(`Comparing against ${picked.owner?.username || "shared"} lap ${best.lap_number}`);
    } catch {
      toast.error("Could not load that session");
    }
  };

  const requestAccess = async () => {
    if (!session?.id) return;
    try {
      const r = await simhubAPI.createShareRequest(session.id, "I'd like to compare laps.");
      if (r.data?.status === "approved") toast.success("Session is public — you can compare now.");
      else toast.success("Request sent to the owner.");
    } catch {
      toast.error("Could not send request");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0" data-testid="session-detail-modal">
        <DialogHeader className="border-b border-border p-4">
          <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-wide">
            <Trophy className="h-5 w-5 text-primary" /> {publicView ? "Shared Session" : "Session Detail"}
          </DialogTitle>
          <DialogDescription>
            {trackLabel} · {carLabel}{carClass ? ` · ${carClass}` : ""}
            {publicView && session?.owner?.username ? ` · by ${session.owner.username}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[80vh] overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="py-10 text-center text-sm text-destructive">{error}</p>
          ) : session ? (
            <div className="space-y-4">
              {sponsors.length > 0 && <SponsorRibbon sponsors={sponsors} />}

              {/* Owner controls */}
              {isOwned && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant={session.is_public ? "default" : "outline"} onClick={togglePublish} disabled={publishing} data-testid="session-publish-toggle">
                    {session.is_public ? <Globe2 className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
                    {session.is_public ? "Published" : "Publish session"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    {session.is_public ? "Visible to all members. Earns points when others compare against it." : "Make this session visible so other members can compare their laps against yours."}
                  </p>
                </div>
              )}
              {publicView && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={requestAccess} data-testid="session-request-access">
                    <Share2 className="mr-1 h-4 w-4" /> Request this driver's full session
                  </Button>
                </div>
              )}

              {/* Meta summary */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatBlock icon={MapPin} label="Track" value={<span className="truncate">{trackLabel}</span>} hint={session.track?.track_config || session.game_name} />
                <StatBlock icon={Car} label="Car" value={<span className="truncate">{carLabel}</span>} hint={carClass || session.session_type} />
                <StatBlock icon={Flag} label="Finish Position" value={session.result?.finish_position ? `P${session.result.finish_position}` : "—"} hint={session.result?.total_participants ? `of ${session.result.total_participants}` : null} />
                <StatBlock icon={Trophy} label="Laps" value={session.total_laps ?? validLaps.length} hint={session.duration_minutes != null ? `${session.duration_minutes} min` : null} />
                <StatBlock icon={Timer} label="Best Lap" value={bestLap ? formatLapTime(bestLap.lap_time_ms) : "—"} hint={bestLap ? `Lap ${bestLap.lap_number}` : null} />
                <StatBlock icon={Timer} label="Average Lap" value={avgLapMs ? formatLapTime(avgLapMs) : "—"} />
                <StatBlock icon={Gauge} label="Top Speed" value={totalMaxSpeed ? `${totalMaxSpeed.toFixed(0)} km/h` : "—"} />
                <StatBlock icon={Flag} label="Started" value={session.started_at ? new Date(session.started_at).toLocaleDateString() : "—"} hint={session.started_at ? new Date(session.started_at).toLocaleTimeString() : null} />
              </div>

              {/* Lap table + overlay selector */}
              {validLaps.length > 0 && (
                <Card className="border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border p-3">
                    <p className="font-display text-sm font-bold uppercase tracking-wide">Laps</p>
                    <Button size="sm" variant="outline" onClick={() => setSelectedLaps(bestLap ? [bestLap.lap_number] : [])} data-testid="session-reset-overlay">
                      Reset to best
                    </Button>
                  </div>
                  <div className="divide-y divide-border">
                    {validLaps.map((lap) => {
                      const isBest = bestLap && lap.lap_number === bestLap.lap_number;
                      const checked = selectedLaps.includes(lap.lap_number);
                      const colorIdx = selectedLaps.indexOf(lap.lap_number);
                      const color = colorIdx >= 0 ? LAP_COLORS[colorIdx % LAP_COLORS.length] : null;
                      const delta = bestLap ? lap.lap_time_ms - bestLap.lap_time_ms : null;
                      return (
                        <label key={lap.lap_number} className="flex cursor-pointer items-center gap-3 p-3 text-sm transition-colors hover:bg-secondary/40" data-testid={`session-lap-${lap.lap_number}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleLap(lap.lap_number)} data-testid={`session-lap-check-${lap.lap_number}`} />
                          {color ? <span className="h-2 w-2 rounded-full" style={{ background: color }} /> : <span className="h-2 w-2 rounded-full bg-muted" />}
                          <span className="w-16 font-mono text-xs text-muted-foreground">Lap {lap.lap_number}</span>
                          <Badge className={`border-0 font-mono ${isBest ? "bg-emerald-500/20 text-emerald-400" : "bg-primary/15 text-primary"}`}>
                            {formatLapTime(lap.lap_time_ms)}{isBest && " · BEST"}
                          </Badge>
                          {!isBest && delta != null && (
                            <span className="text-xs text-muted-foreground">{formatDeltaMs(delta)}</span>
                          )}
                          {lap.max_speed_kmh ? <span className="ml-auto text-xs text-muted-foreground">{lap.max_speed_kmh.toFixed(0)} km/h top</span> : null}
                        </label>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Analytics panel for first selected lap */}
              {activeLaps[0] && (activeLaps[0].samples || []).length > 0 && (
                <AnalyticsPanel lap={activeLaps[0]} bestLap={bestLap} />
              )}

              {/* X-axis toggle + Compare picker */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2" data-testid="session-xaxis-toggle">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">X axis</span>
                  <div className="inline-flex overflow-hidden rounded border border-border">
                    <button type="button" onClick={() => setXAxis("time")} className={`px-3 py-1 text-xs ${xAxis === "time" ? "bg-primary text-primary-foreground" : "bg-card text-foreground"}`} data-testid="session-xaxis-time">Time</button>
                    <button type="button" onClick={() => setXAxis("position")} className={`px-3 py-1 text-xs ${xAxis === "position" ? "bg-primary text-primary-foreground" : "bg-card text-foreground"}`} data-testid="session-xaxis-position">Position</button>
                  </div>
                </div>
                <ComparePicker onPick={onPickCompare} />
              </div>

              {compare?.lap && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 p-2 text-xs">
                  <span className="h-2 w-4" style={{ background: COMPARE_COLOR }} />
                  <span className="font-semibold">Comparing against:</span>
                  <span>{compare.session?.owner?.username || "Shared"} · Lap {compare.lap.lap_number} ({formatLapTime(compare.lap.lap_time_ms)})</span>
                  <Button size="sm" variant="ghost" onClick={() => setCompare(null)} className="ml-auto h-7">Clear</Button>
                </div>
              )}

              {/* Charts */}
              {anySamples ? (
                <div className="space-y-4">
                  <ChartCard title="Speed (km/h)" data={speedData} selectedLaps={selectedLaps} unit="km/h" xAxis={xAxis} hasCompare={!!compare?.lap} />
                  <ChartCard title="Throttle (%)" data={throttleData} selectedLaps={selectedLaps} unit="%" domain={[0, 100]} xAxis={xAxis} hasCompare={!!compare?.lap} />
                  <ChartCard title="Brake (%)" data={brakeData} selectedLaps={selectedLaps} unit="%" domain={[0, 100]} xAxis={xAxis} hasCompare={!!compare?.lap} />
                </div>
              ) : (
                <Card className="border-dashed border-border bg-card p-5 text-center text-xs text-muted-foreground" data-testid="session-no-samples">
                  Lap telemetry samples will appear here for any new session you record from the mobile app. Older sessions were saved before per-lap samples were captured.
                </Card>
              )}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
